import { useMemo, useState, useEffect, useId } from "react";
import { Link } from "react-router-dom";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    SortingState,
    ColumnFiltersState,
} from "@tanstack/react-table";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

import {
    Search,
    Filter,
    Eye,
    ArrowUpDown,
    X as XIcon,
    Package,
} from "lucide-react";

// Removed LoadingState
// import { LoadingState } from "@/pages/CustomerPortal/components/LoadingState";
import { useQuery } from "@tanstack/react-query";
import { administrativeApi, clientsApi, School } from "@/lib/api";
import { getAccountIdSync } from "@/lib/oidcAuth";
import { TooltipWrapper } from "@/components/ui/TooltipWrapper";
import { AdimplenciaTag } from "@/components/shared/AdimplenciaTag";

// Added ClientsListSkeleton and EmptyState
import { ClientsListSkeleton } from "./component/ClientsListSkeleton";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { cn } from "@/lib/utils";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";

const NO_GROUP_FILTER = "__sem_grupo__";

// Grupo exibido na lista: o vinculado em customer_group ou, se ainda não houver, o texto legado.
const getGroupLabel = ( client: School ) => client.customerGroup?.name || client.clientCustomerGroup || "";

export default function ClientsList() {

    const [ searchQuery, setSearchQuery ] = useState( "" );
    const [ financialStatusFilter, setFinancialStatusFilter ] = useState<string>( "all" );
    const [ regionFilter, setRegionFilter ] = useState<string>( "all" );
    const [ groupFilter, setGroupFilter ] = useState<string>( "all" );

    // IDs for ARIA
    const searchInputId = useId();
    const regionFilterId = useId();
    const groupFilterId = useId();
    const itemsPerPageLabelId = useId();


    useEffect( () => {
        const savedFiltersJSON = sessionStorage.getItem( 'clientsListFilters' );
        if ( savedFiltersJSON ) {
            try {
                const savedFilters = JSON.parse( savedFiltersJSON );
                if ( savedFilters.searchQuery ) setSearchQuery( savedFilters.searchQuery );
                if ( savedFilters.financialStatusFilter ) setFinancialStatusFilter( savedFilters.financialStatusFilter );
                if ( savedFilters.regionFilter ) setRegionFilter( savedFilters.regionFilter );
                if ( isCustomerGroupAddressEnabled && savedFilters.groupFilter ) setGroupFilter( savedFilters.groupFilter );
            } catch ( error ) {
                console.error( "Erro ao restaurar filtros da sessionStorage:", error );
                sessionStorage.removeItem( 'clientsListFilters' );
            }
        }
    }, [] );

    // Memoized value for active filters
    const hasActiveFilters = useMemo( () => {
        return searchQuery !== "" || financialStatusFilter !== 'all' || regionFilter !== 'all' || groupFilter !== 'all';
    }, [ searchQuery, financialStatusFilter, regionFilter, groupFilter ] );

    useEffect( () => {
        const filters = {
            searchQuery,
            financialStatusFilter,
            regionFilter,
            groupFilter,
        };

        if ( hasActiveFilters ) {
            sessionStorage.setItem( 'clientsListFilters', JSON.stringify( filters ) );
        } else {
            sessionStorage.removeItem( 'clientsListFilters' );
        }
    }, [ searchQuery, financialStatusFilter, regionFilter, groupFilter, hasActiveFilters ] );

    const [ sorting, setSorting ] = useState<SortingState>( [] );
    const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>( [] );
    const [ pagination, setPagination ] = useState( {
        pageIndex: 0,
        pageSize: 10,
    } );

    const {
        data: allclients = [],
        isLoading,
        error,
        refetch,
        isFetching,
    } = useQuery<School[]>( {
        queryKey: [ "clients" ],
        queryFn: async () => {
            const accountId = getAccountIdSync();
            if ( !accountId ) throw new Error( "Account ID not found in session storage" );

            await administrativeApi.register();

            const { data } = await clientsApi.getSchoolsByAccount( accountId );
            return data || [];
        },
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
    } );

    const filteredData = useMemo( () => {
        let filtered = [ ...allclients ];
        const lowerSearchQuery = searchQuery.toLowerCase();

        const extrairNumeros = ( texto: string ) => ( texto || '' ).replace( /\D/g, '' );

        if ( searchQuery ) {
            filtered = filtered.filter( client =>
                ( client.clientCodeCnx || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientCompanyName || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientEmail || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientFantasyName || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientState || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientCity || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientCnpj || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.clientCustomerGroup || '' ).toLowerCase().includes( lowerSearchQuery )
                || ( client.customerGroup?.name || '' ).toLowerCase().includes( lowerSearchQuery )
                || extrairNumeros( client.clientCnpj ).includes( lowerSearchQuery )
            );
        }

        if ( regionFilter !== "all" ) {
            filtered = filtered.filter( client => client.clientState === regionFilter );
        }

        if ( isCustomerGroupAddressEnabled && groupFilter !== "all" ) {
            filtered = filtered.filter( client => groupFilter === NO_GROUP_FILTER
                ? !getGroupLabel( client )
                : getGroupLabel( client ) === groupFilter );
        }

        return filtered;
    }, [ allclients, searchQuery, regionFilter, groupFilter ] );

    const groupOptions = useMemo(
        () => [ ...new Set( allclients.map( getGroupLabel ).filter( Boolean ) ) ].sort( ( a, b ) => a.localeCompare( b, "pt-BR" ) ),
        [ allclients ]
    );

    const handleClearFilters = () => {
        setSearchQuery( "" );
        setFinancialStatusFilter( "all" );
        setRegionFilter( "all" );
        setGroupFilter( "all" );
    };

    const columns: ColumnDef<School>[] = [
        {
            accessorKey: "clientCodeCnx",
            header: ( { column } ) => (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting( column.getIsSorted() === "asc" )}
                    className="px-0 hover:bg-transparent"
                >
                    Código
                    <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
            ),
            cell: ( { row } ) => (
                <div className="font-mono font-medium">{row.getValue( "clientCodeCnx" )}</div>
            ),
        },
        {
            accessorKey: "clientCompanyName",
            header: ( { column } ) => (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting( column.getIsSorted() === "asc" )}
                    className="px-0 hover:bg-transparent"
                >
                    Nome da Escola
                    <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
            ),
            cell: ( { row } ) => {
                return (
                    <div className="flex-row items-center gap-2">
                        <div className="font-medium">{row.getValue( "clientCompanyName" )}</div>
                        <div className="font-medium text-muted-foreground">{row.original.clientFantasyName}</div>
                    </div>
                );
            },
        },
        {
            id: "status",
            header: "Status",
            cell: ( { row } ) => (
                <AdimplenciaTag isInadimplente={row.original.isInadimplente} />
            ),
        },
        {
            accessorKey: "clientEmail",
            header: "Contato",
            cell: ( { row } ) => (
                <div className="text-sm">
                    <div className="text-foreground">{row.original.clientEmail}</div>
                </div>
            ),
        },
        ...( isCustomerGroupAddressEnabled
            ? [ {
                id: "customerGroup",
                accessorFn: getGroupLabel,
                header: "Grupo",
                cell: ( { row } ) => {
                    const label = getGroupLabel( row.original );
                    if ( !label ) return <span className="text-sm text-muted-foreground">—</span>;
                    return (
                        <div className="text-sm">
                            <div>{label}</div>
                            {!row.original.customerGroup && (
                                <div className="text-xs text-muted-foreground">sem grupo vinculado</div>
                            )}
                        </div>
                    );
                },
            } satisfies ColumnDef<School> ]
            : [] ),
        {
            accessorKey: "region",
            header: "Região",
            cell: ( { row } ) => (
                <Badge variant="outline">{`${ row.original.clientCity }/${ row.original.clientState }`.toUpperCase()}</Badge>
            ),
        },
        {
            accessorKey: "shippingFee",
            header: "Frete",
            cell: ( { row } ) => (
                <Badge variant="default">{`${ row.original.shippingFee }%`.toUpperCase()}</Badge>
            ),
        },
        {
            id: "actions",
            header: () => <div className="text-right">Ações</div>,
            cell: ( { row } ) => {
                const client = row.original;
                return (
                    <div className="flex justify-end gap-1">
                        <TooltipWrapper content="Ver detalhes">
                            <Button variant="ghost" size="sm" asChild aria-label={`Ver detalhes do cliente ${ client.clientCompanyName }`}>
                                <Link to={`/backoffice/client/${ row.original.clientCodeCnx }`}><Eye className="h-4 w-4" aria-hidden="true" /></Link>
                            </Button>
                        </TooltipWrapper>
                    </div>
                );
            },
        },
    ];

    const table = useReactTable( {
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        state: {
            sorting,
            columnFilters,
            pagination,
        },
    } );

    if ( isLoading ) {
        return <ClientsListSkeleton />;
    }

    const stateNames: Record<string, string> = {
        "AC": "Acre",
        "AL": "Alagoas",
        "AP": "Amapá",
        "AM": "Amazonas",
        "BA": "Bahia",
        "CE": "Ceará",
        "DF": "Distrito Federal",
        "ES": "Espírito Santo",
        "GO": "Goiás",
        "MA": "Maranhão",
        "MT": "Mato Grosso",
        "MS": "Mato Grosso do Sul",
        "MG": "Minas Gerais",
        "PA": "Pará",
        "PB": "Paraíba",
        "PR": "Paraná",
        "PE": "Pernambuco",
        "PI": "Piauí",
        "RJ": "Rio de Janeiro",
        "RN": "Rio Grande do Norte",
        "RS": "Rio Grande do Sul",
        "RO": "Rondônia",
        "RR": "Roraima",
        "SC": "Santa Catarina",
        "SP": "São Paulo",
        "SE": "Sergipe",
        "TO": "Tocantins",
    };

    return (
        <>
            <div className="min-h-screen bg-muted dark:bg-background">
                {/* Header */}
                <header className="border-b bg-foreground text-background">
                    <div className="container mx-auto px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight">Gestão de Clientes</h1>
                                <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
                                    {filteredData.length} cliente{filteredData.length !== 1 ? 's' : ''} encontrado{filteredData.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="container mx-auto px-6 py-8 space-y-6">
                    {/* Filtros */}
                    <Card className={cn( "border-border shadow-lg", hasActiveFilters && "border-primary/50 bg-primary/5" )}>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                <Filter className="h-5 w-5" aria-hidden="true" />
                                Filtros
                            </CardTitle>
                            <Button variant="ghost" size="sm" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                                <XIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                Limpar Filtros
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-4">
                                {/* Busca por nome ou código */}
                                <div className={isCustomerGroupAddressEnabled ? "md:col-span-2" : "md:col-span-3"}>
                                    <Label htmlFor={searchInputId} className="sr-only">Buscar cliente</Label>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                        <Input
                                            id={searchInputId}
                                            placeholder="Buscar por nome ou código do cliente..."
                                            value={searchQuery}
                                            onChange={( e ) => setSearchQuery( e.target.value )}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                {/* Filtro de Região */}
                                <div>
                                    <Label htmlFor={regionFilterId} className="sr-only">Filtrar por região</Label>
                                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                                        <SelectTrigger id={regionFilterId}>
                                            <SelectValue placeholder="Região">
                                                {regionFilter === "all" ? "Todas as Regiões" : stateNames[ regionFilter ]}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todas as Regiões</SelectItem>
                                            {Object.entries( stateNames ).map( ( [ abbr, name ] ) => (
                                                <SelectItem key={abbr} value={abbr}>{name}</SelectItem>
                                            ) )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Filtro de Grupo */}
                                {isCustomerGroupAddressEnabled && (
                                    <div>
                                        <Label htmlFor={groupFilterId} className="sr-only">Filtrar por grupo</Label>
                                        <Select value={groupFilter} onValueChange={setGroupFilter}>
                                            <SelectTrigger id={groupFilterId}>
                                                <SelectValue placeholder="Grupo">
                                                    {groupFilter === "all"
                                                        ? "Todos os Grupos"
                                                        : groupFilter === NO_GROUP_FILTER ? "Sem grupo" : groupFilter}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todos os Grupos</SelectItem>
                                                <SelectItem value={NO_GROUP_FILTER}>Sem grupo</SelectItem>
                                                {groupOptions.map( ( group ) => (
                                                    <SelectItem key={group} value={group}>{group}</SelectItem>
                                                ) )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tabela de Clientes */}
                    <Card className="border-border shadow-lg">
                        <CardContent className="p-0">
                            <div className="rounded-md border border-border" aria-busy={isFetching}>
                                <Table>
                                    <caption className="sr-only">Lista de clientes com opções de busca e filtro.</caption>
                                    <TableHeader>
                                        {table.getHeaderGroups().map( ( headerGroup ) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map( ( header ) => (
                                                    <TableHead
                                                        key={header.id}
                                                        scope="col"
                                                        aria-sort={
                                                            header.column.getCanSort()
                                                                ? header.column.getIsSorted() === 'asc'
                                                                    ? 'ascending'
                                                                    : header.column.getIsSorted() === 'desc'
                                                                        ? 'descending'
                                                                        : 'none'
                                                                : undefined
                                                        }
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </TableHead>
                                                ) )}
                                            </TableRow>
                                        ) )}
                                    </TableHeader>
                                    <TableBody>
                                        {table.getRowModel().rows?.length ? (
                                            table.getRowModel().rows.map( ( row ) => (
                                                <TableRow
                                                    key={row.id}
                                                    className="cursor-pointer"
                                                >
                                                    {row.getVisibleCells().map( ( cell ) => (
                                                        <TableCell key={cell.id}>
                                                            {flexRender( cell.column.columnDef.cell, cell.getContext() )}
                                                        </TableCell>
                                                    ) )}
                                                </TableRow>
                                            ) )
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={columns.length}>
                                                    <EmptyState
                                                        title="Nenhum cliente encontrado"
                                                        message="Tente ajustar seus filtros."
                                                        className="py-8"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Controles de Paginação */}
                    <Card className="border-border">
                        <CardContent className="pt-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                {/* Controle de itens por página */}
                                <div className="flex items-center gap-2">
                                    <Label htmlFor={itemsPerPageLabelId} className="text-sm text-muted-foreground">
                                        Itens por página:
                                    </Label>
                                    <Select
                                        value={pagination.pageSize.toString()}
                                        onValueChange={( value ) => {
                                            table.setPageSize( Number( value ) );
                                        }}
                                    >
                                        <SelectTrigger id={itemsPerPageLabelId} className="w-[80px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="10">10</SelectItem>
                                            <SelectItem value="25">25</SelectItem>
                                            <SelectItem value="50">50</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Info de paginação */}
                                <div className="text-sm text-muted-foreground">
                                    Página {table.getState().pagination.pageIndex + 1} de{" "}
                                    {table.getPageCount() || 1}
                                </div>

                                {/* Botões de navegação */}
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => table.previousPage()}
                                        disabled={!table.getCanPreviousPage()}
                                    >
                                        <span className="sr-only">Página </span>Anterior
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => table.nextPage()}
                                        disabled={!table.getCanNextPage()}
                                    >
                                        <span className="sr-only">Página </span>Próxima
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </main>
            </div>
        </>
    );
}